'use client';

import React, { useEffect } from 'react';
import { Container } from 'components/shared';
import { BuyNowButton } from 'components/shared';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import {
  CheckCircle,
  HsaBadge,
  UhLogoWithType,
  ClueLogoWithType,
  HsaMobileBadge,
} from './HeroIcons';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import useCluePricing from 'hooks/pricing/useCluePricing';
import {
  PrimaryOffers,
  PrimaryOffersTransitionTile,
  OffersDataType,
} from 'components/ring-air/ProductSelectionSection/PrimaryOffers';
import { useWindowSize } from 'hooks/dom/useWindowSize';
import { PHONE_MAX_WIDTH_IN_PX } from 'components/theme';
import { OffersModalNewProvider } from 'components/shared/OffersModal';
import styles from './Hero.module.css';

const Hero = () => {
  const { bundlePriceDisplay } = useCluePricing();
  const { region: country } = useRegionLocale();
  const { width } = useWindowSize();

  const isUS = country === 'US' || country === 'PR';
  const isMobile = width <= PHONE_MAX_WIDTH_IN_PX;

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const applyOrRemoveOffset = () => {
      if (!isMobile) {
        document.body.classList.remove('hero-chatbot-offset');
        return;
      }
      if (window.scrollY > 40) {
        document.body.classList.remove('hero-chatbot-offset');
      } else {
        document.body.classList.add('hero-chatbot-offset');
      }
    };

    // Initial apply on mount and on width change
    applyOrRemoveOffset();

    // Listen to scroll changes
    window.addEventListener('scroll', applyOrRemoveOffset, { passive: true });

    return () => {
      window.removeEventListener(
        'scroll',
        applyOrRemoveOffset as EventListener,
      );
      document.body.classList.remove('hero-chatbot-offset');
    };
  }, [isMobile]);

  const COUNTRY_OFFERS: Record<string, OffersDataType[]> = {
    // Default (US/global)
    global: [
      {
        title: <>Express checkout with PayPal</>,
        image: ['web_v2/icons/primary_offers/paypal.png'],
      },
      {
        title: <>Instant payment with Apple Pay</>,
        image: ['web_v2/icons/primary_offers/applepay.png'],
      },
    ],
    US: [
      {
        title: <>Save more with your pre-tax dollars</>,
        image: ['web_v2/icons/hsa-fsa/hsa-fsa-black-outline.svg'],
      },
      {
        title: (
          <>
            As low as 0 APR financing |{' '}
            <a
              href="https://www.klarna.com"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{ color: 'inherit', textDecoration: 'underline' }}
            >
              Klarna
            </a>
            , AfterPay & Affirm
          </>
        ),
        image: [
          'web_v2/icons/primary_offers/afterpay.png',
          'web_v2/icons/primary_offers/klara.png',
          'web_v2/icons/primary_offers/affirmm.png',
        ],
      },
      {
        title: <>Shop Pay | Pay in full or in parts</>,
        image: ['web_v2/icons/primary_offers/shoppay.png'],
        imageType: 'square',
      },
      {
        title: <>Instant payment with PayPal, Apple Pay, or GPay</>,
        image: [
          'web_v2/icons/primary_offers/paypal.png',
          'web_v2/icons/primary_offers/applepay.png',
          'web_v2/icons/primary_offers/gpay.png',
        ],
      },
    ],
    GB: [
      {
        title: <>One-tap checkout with Shop Pay</>,
        image: ['web_v2/icons/primary_offers/shoppay.png'],
      },
      {
        title: <>Instant payment with PayPal</>,
        image: ['web_v2/icons/primary_offers/paypal.png'],
        imageType: 'square',
      },
      {
        title: <>As low as 0 APR financing | Klarna</>,
        image: ['web_v2/icons/primary_offers/klara.png'],
        imageType: 'square',
      },
    ],
    CA: [
      {
        title: <>Express checkout with PayPal</>,
        image: ['web_v2/icons/primary_offers/paypal.png'],
      },
      {
        title: <>Instant payment with Apple Pay</>,
        image: ['web_v2/icons/primary_offers/applepay.png'],
      },
    ],
    JP: [
      {
        title: <>Express checkout with PayPal</>,
        image: ['web_v2/icons/primary_offers/paypal.png'],
      },
      {
        title: <>Instant payment with Apple Pay</>,
        image: ['web_v2/icons/primary_offers/applepay.png'],
      },
    ],
    IN: [
      {
        title: <>No-cost EMI starting at ₹ 1,187/month</>,
        image: ['web_v2/icons/primary_offers/0-percent.png'],
      },
      {
        title: <>CRED pay | Save and earn rewards</>,
        image: ['web_v2/icons/primary_offers/cred.png'],
      },
      {
        title: <>Easy payments with UPI</>,
        image: ['web_v2/icons/primary_offers/upi.png'],
      },
    ],
    AE: [
      {
        title: <>Instant payment with Apple Pay</>,
        image: ['web_v2/icons/primary_offers/applepay.png'],
      },
      {
        title: <>Split payments with Tabby, Tamara or Cashew</>,
        image: [
          'web_v2/icons/primary_offers/icon.png',
          'web_v2/icons/primary_offers/tamara_new.png',
          'web_v2/icons/primary_offers/cashew.png',
        ],
      },
    ],
    SA: [
      {
        title: <>Instant payment with Apple Pay</>,
        image: ['web_v2/icons/primary_offers/applepay.png'],
      },
      {
        title: <>Split payments with Tabby, Tamara or Cashew</>,
        image: [
          'web_v2/icons/primary_offers/icon.png',
          'web_v2/icons/primary_offers/tamara_new.png',
          'web_v2/icons/primary_offers/cashew.png',
        ],
      },
    ],
  };

  let offersData: OffersDataType[] = COUNTRY_OFFERS.global;
  if (
    country &&
    Object.prototype.hasOwnProperty.call(COUNTRY_OFFERS, country)
  ) {
    offersData = COUNTRY_OFFERS[country as keyof typeof COUNTRY_OFFERS];
  }

  return (
    <OffersModalNewProvider>
      <section
        className={styles.heroSection}
        aria-label="Hero section: Ultrahuman × Clue bundle"
        style={
          {
            ['--hero-bg-mobile' as string]: `url(${getCompressedAssetUrl(
              '/web_v2/uh-x-clue/hero/mob-banner-scaled.png',
            )})`,
            ['--hero-bg-desktop' as string]: `url(${getCompressedAssetUrl(
              '/web_v2/uh-x-clue/hero/web-hero-new.png',
            )})`,
          } as React.CSSProperties
        }
      >
        <Container>
          <div className={styles.content}>
            <div className={styles.leftContent}>
              <div
                className={styles.brandRow}
                aria-label="Ultrahuman × Clue"
                role="group"
              >
                <UhLogoWithType className={styles.uhLogoType} />
                <span className={styles.divider} />
                <ClueLogoWithType className={styles.clueLogoType} />
              </div>
              <h1 className={styles.title}>
                Where your biomarkers meet cycle intelligence
              </h1>

              <div className={styles.ctaRow}>
                <BuyNowButton
                  className={styles.heroBuyNowButton}
                  href={{ pathname: '/ring/buy', query: { flow: 'clue' } }}
                  aria-label={`Buy Ultrahuman Ring AIR and Clue Plus bundle for ${bundlePriceDisplay}`}
                >
                  Buy bundle for {bundlePriceDisplay}
                </BuyNowButton>

                {isUS && (
                  <div
                    className={styles.badgeRow}
                    role="note"
                    aria-label="Eligible for HSA/FSA in the United States"
                  >
                    <span className={styles.hsaBadgeMobile}>
                      <HsaMobileBadge />
                    </span>
                    <span className={styles.hsaBadgeDesktop}>
                      <CheckCircle aria-hidden="true" />
                      <HsaBadge />
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Container>
        <div
          className={styles.offersBottomBar}
          aria-label="Payment and checkout options"
        >
          <div className={styles.offersDesktop}>
            <PrimaryOffers data={offersData} $compact $transparent />
          </div>
          <div className={styles.offersMobile}>
            <PrimaryOffersTransitionTile
              data={offersData}
              $compact
              $transparent
            />
          </div>
        </div>
      </section>
    </OffersModalNewProvider>
  );
};

export default Hero;
